import React from "react";
import { Badge, Button, Icon, Popover, Tooltip, Input } from "antd";

import { ModuleHeader } from "~/components/ModuleHeader";
import FilterPillsBar from "~/_shared/components/FilterPillsBar";
import MonthPicker from "~/_shared/components/MonthPicker";
import RestoredFiltersNotice from "~/_shared/components/RestoredFiltersNotice";
import { formatPeriodLabel } from "./ParcelasIndicator";
import { isFinanceiroPeriodDefault, countActiveFinanceiroFilters } from "../filters";

var { Search } = Input;

var SCREEN_MAP = {
  active: { label: "Ativos", color: "#52c41a", icon: "check-circle" },
  archived: { label: "Arquivados", color: "#8c8c8c", icon: "file-done" },
};

// Status de pagamento. "open"/"overdue" ignoram o mês (dívida de qualquer mês).
var STATUS_OPTIONS = [
  { value: "open", label: "Em aberto", color: "#fa8c16", icon: "clock-circle", hint: "Tudo que falta pagar, de qualquer mês" },
  { value: "overdue", label: "Vencidos", color: "#cf1322", icon: "exclamation-circle", hint: "Só parcelas vencidas/inadimplentes" },
  { value: "paid", label: "Quitados", color: "#52c41a", icon: "check-circle", hint: "Já pagos (respeita o mês)" },
  { value: "all", label: "Todos", color: "#1890ff", icon: "profile", hint: "Todos os tickets (respeita o mês)" },
];

var statusInfo = function(value) {
  for (var i = 0; i < STATUS_OPTIONS.length; i++) {
    if (STATUS_OPTIONS[i].value === value) {
      return STATUS_OPTIONS[i];
    }
  }
  return STATUS_OPTIONS[0];
};

export var FinanceiroHeader = function(props) {
  var list = props.list;
  var screen = props.screen;
  var setScreen = props.setScreen;
  var search = props.search || "";
  var setSearch = props.setSearch;
  var selectedMonth = props.selectedMonth;
  var onMonthChange = props.onMonthChange;
  var customRange = props.customRange;
  var onCustomRange = props.onCustomRange;
  var paymentStatus = props.paymentStatus || "open";
  var setPaymentStatus = props.setPaymentStatus;
  var monthApplies = props.monthApplies;
  // Filtros aplicados/salvos (usePersistedFilters, dono é o Dashboard).
  var persisted = props.persisted;
  var filters = persisted.filters;
  var defaults = persisted.defaults;

  var activeCount =
    (list && list.not_started ? list.not_started.length : 0) +
    (list && list.in_process ? list.in_process.length : 0) +
    (list && list.done ? list.done.length : 0);
  var archivedCount = list && list.archived ? list.archived.length : 0;

  var isPeriodDefault = isFinanceiroPeriodDefault(filters, defaults);

  // Badge: só o que difere do padrão (status "Em aberto", Ativos, mês atual, sem busca).
  var filterCount = countActiveFinanceiroFilters(filters, defaults);

  // Pills mostram os valores aplicados; remover uma volta só aquele filtro ao padrão.
  var pills = [];
  var sInfo = statusInfo(paymentStatus);
  pills.push({
    key: "status", label: sInfo.label, color: sInfo.color, icon: sInfo.icon,
    onRemove: paymentStatus === defaults.paymentStatus ? undefined : function() { setPaymentStatus(defaults.paymentStatus); },
  });
  var screenInfo = SCREEN_MAP[screen] || SCREEN_MAP.active;
  pills.push({
    key: "screen", label: screenInfo.label, color: screenInfo.color, icon: screenInfo.icon,
    onRemove: screen === defaults.screen ? undefined : function() { setScreen(defaults.screen); },
  });
  // O mês escopa sempre o resumo (KPIs) — por isso a pill diz "Resumo: ...".
  var monthLabel = formatPeriodLabel(customRange || selectedMonth, false);
  pills.push({
    key: "month", label: "Resumo: " + monthLabel, color: customRange ? "#722ed1" : "#1890ff", icon: "calendar",
    onRemove: isPeriodDefault ? undefined : function() { onMonthChange(defaults.selectedMonth); },
  });
  if (search) {
    pills.push({ key: "search", label: "Busca: " + search, color: "#722ed1", icon: "search", onRemove: function() { setSearch(""); } });
  }

  var isDefault = persisted.isDefault;

  var handleClearAllFilters = function() {
    persisted.resetFilters();
  };

  var restoredSummary = pills
    .filter(function(pill) { return !!pill.onRemove; })
    .map(function(pill) { return pill.label; })
    .join(" · ");

  return (
    <>
    <ModuleHeader
      breadcrumbs={["Dep. Financeiro", "Painel"]}
      title={
        <span>
          Departamento Financeiro{" "}
          <Tooltip title="Acompanhe os serviços extras por etapa e veja, visualmente, quantas parcelas já foram pagas — identificando tickets concluídos que ainda estão em aberto.">
            <small>
              <Icon type="info-circle" />
            </small>
          </Tooltip>
        </span>
      }
      search={
        <Search
          placeholder="Pesquisar por cód ou cliente"
          value={search}
          onChange={function(e) { setSearch(e.target.value); }}
          style={{ maxWidth: 300 }}
        />
      }
      center={
        <Popover
          trigger="click"
          placement="bottomRight"
          content={
            <div style={{ width: 320 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: "#262626" }}>Filtros</span>
                {!isDefault && (
                  <a style={{ fontSize: 12, color: "#f5222d" }} onClick={handleClearAllFilters}>
                    Limpar tudo
                  </a>
                )}
              </div>

              {/* Status de pagamento — a lente principal de cobrança */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Status de pagamento
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {STATUS_OPTIONS.map(function(opt) {
                    var isActive = paymentStatus === opt.value;
                    return (
                      <Tooltip key={opt.value} title={opt.hint}>
                        <div
                          onClick={function() { setPaymentStatus(opt.value); }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "5px 10px",
                            borderRadius: 6,
                            cursor: "pointer",
                            border: "1.5px solid " + (isActive ? opt.color : "#e8e8e8"),
                            background: isActive ? opt.color + "0a" : "#fafafa",
                            fontSize: 13,
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? "#262626" : "#8c8c8c",
                          }}
                        >
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: opt.color, display: "inline-block" }} />
                          {opt.label}
                          {isActive && <Icon type="check" style={{ color: opt.color, fontSize: 13 }} />}
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>

              {/* Mês — escopa o RESUMO (KPIs) sempre; e a lista em Todos/Quitados. */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em", display: "flex", justifyContent: "space-between" }}>
                  <span>Período do resumo</span>
                  {(selectedMonth || customRange) && (
                    <a style={{ fontSize: 11, color: "#8c8c8c" }} onClick={function() { onMonthChange(null); }}>Todos os meses</a>
                  )}
                </div>
                <div style={{ fontSize: 11, color: "#bfbfbf", marginBottom: 8, textTransform: "none" }}>
                  {monthApplies
                    ? "Define os KPIs e também filtra a lista nesta lente."
                    : "Define os KPIs do topo. A lista (Em aberto/Vencidos) mostra todos os meses."}
                </div>
                <MonthPicker
                  value={selectedMonth}
                  onChange={function(value) { onMonthChange(value); }}
                  onCustomRange={onCustomRange}
                  customRangeLabel={customRange ? formatPeriodLabel(customRange, false) : null}
                  showTodayButton
                  align="left"
                />
              </div>

              <div>
                <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Visualização
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {[
                    { value: "active", label: "Ativos", color: "#52c41a", count: activeCount },
                    { value: "archived", label: "Arquivados", color: "#8c8c8c", count: archivedCount },
                  ].map(function(opt) {
                    var isActive = screen === opt.value;
                    return (
                      <div
                        key={opt.value}
                        onClick={function() { setScreen(opt.value); }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "6px 10px",
                          borderRadius: 6,
                          cursor: "pointer",
                          border: "1.5px solid " + (isActive ? opt.color : "#e8e8e8"),
                          background: isActive ? opt.color + "0a" : "#fafafa",
                        }}
                      >
                        <span style={{ fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? "#262626" : "#8c8c8c" }}>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: opt.color, display: "inline-block", marginRight: 8 }} />
                          {opt.label}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: isActive ? opt.color : "#bfbfbf" }}>{opt.count}</span>
                          {isActive && <Icon type="check" style={{ color: opt.color, fontSize: 14 }} />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          }
        >
          <Badge count={filterCount} offset={[-4, 0]} style={{ backgroundColor: "#722ed1" }}>
            <Button icon="bars" style={{ borderRadius: 8 }}>
              Filtros
            </Button>
          </Badge>
        </Popover>
      }
      advanced={React.createElement(FilterPillsBar, {
        pills: pills,
        onClearAll: isDefault ? undefined : handleClearAllFilters,
      })}
    />
    <RestoredFiltersNotice
      visible={persisted.wasRestored}
      summary={restoredSummary}
      onReset={handleClearAllFilters}
      onDismiss={persisted.dismissRestored}
    />
    </>
  );
};

export default FinanceiroHeader;
